import PropTypes from 'prop-types';
import {useCallback, useEffect, useMemo} from 'react';
import {Button, TextArea} from '@/components/atoms';
import {
  ImageUploadField,
  MultiLanguageFieldGroup,
  PageHeader,
  TextInputField
} from '@/components/molecules';
import {PageContainer} from '@/components/organisms';
import {updateLanguageFieldMap} from '@/pages/PerfectStoreUtils';
import {FONT_SIZE} from '@/utils/constants/globalStyle';
import {MAX_RACK_DISPLAYS} from '@/utils/constants/perfectStoreConstants';
import RackDisplayCard from './RackDisplayCard';
import {
  bannerDescriptionUiSchema,
  bannerImageHelperText,
  bannerTitleUiSchema,
  STRING_SCHEMA
} from './schemas';

const createEmptyRackDisplay = () => ({
  id: crypto.randomUUID(),
  image: null,
  content: {}
});

const PerfectStoreDisplay = ({formData, setFormData, languages}) => {
  const displayContent = formData?.displayContent || {
    bannerImage: null,
    bannerText: {},
    rackDisplays: []
  };
  const rackDisplays = useMemo(
    () => displayContent.rackDisplays || [],
    [displayContent.rackDisplays]
  );

  const persistDisplayContent = useCallback(
    (partial) => {
      setFormData((prev) => ({
        ...prev,
        displayContent: {
          ...prev?.displayContent,
          ...partial
        }
      }));
    },
    [setFormData]
  );

  useEffect(() => {
    if (rackDisplays.length === 0) {
      persistDisplayContent({rackDisplays: [createEmptyRackDisplay()]});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleBannerImageChange = useCallback(
    (image) => persistDisplayContent({bannerImage: image}),
    [persistDisplayContent]
  );

  const handleBannerImageDelete = useCallback(
    () => persistDisplayContent({bannerImage: null}),
    [persistDisplayContent]
  );

  const handleBannerTextChange = useCallback(
    (langCode, field, value, hasError) => {
      persistDisplayContent({
        bannerText: updateLanguageFieldMap(
          displayContent.bannerText,
          langCode,
          field,
          value,
          hasError
        )
      });
    },
    [displayContent.bannerText, persistDisplayContent]
  );

  const handleRackChange = useCallback(
    (index, updater) => {
      setFormData((prev) => {
        const rackDisplays = prev?.displayContent?.rackDisplays || [];
        const currentRack = rackDisplays[index];
        const updatedRack = updater(currentRack);
        const updated = rackDisplays.map((rack, i) =>
          i === index ? updatedRack : rack
        );
        return {
          ...prev,
          displayContent: {
            ...prev?.displayContent,
            rackDisplays: updated
          }
        };
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const handleRackRemove = useCallback(
    (index) => {
      const updated = rackDisplays.filter((_, i) => i !== index);
      persistDisplayContent({rackDisplays: updated});
    },
    [rackDisplays, persistDisplayContent]
  );

  const handleAddRackDisplay = useCallback(() => {
    persistDisplayContent({
      rackDisplays: [...rackDisplays, createEmptyRackDisplay()]
    });
  }, [rackDisplays, persistDisplayContent]);

  return (
    <PageContainer data-testid='perfect-store-display-step'>
      <PageHeader title='Banner Image' fontSize={FONT_SIZE.LG} />
      <ImageUploadField
        label='First Position Image*'
        tooltipText={bannerImageHelperText}
        required
        imageUri={
          displayContent.bannerImage?.imageUri ||
          displayContent.bannerImage?.imageUrl
        }
        onChange={handleBannerImageChange}
        onDelete={handleBannerImageDelete}
        dataTestId='banner-image-upload'
      />

      <PageHeader title='Banner Text' fontSize={FONT_SIZE.LG} />
      <MultiLanguageFieldGroup
        languages={languages}
        dataTestId='banner-text-fields'
        renderField={(lang) => {
          const data = displayContent.bannerText?.[lang.code] || {};
          return (
            <>
              <TextInputField
                value={data.title || ''}
                label={bannerTitleUiSchema.label}
                schema={STRING_SCHEMA}
                uiSchema={bannerTitleUiSchema}
                updateValue={(val, hasError) =>
                  handleBannerTextChange(lang.code, 'title', val, hasError)
                }
                visible
              />
              <TextArea
                value={data.description || ''}
                label={bannerDescriptionUiSchema.label}
                uiSchema={bannerDescriptionUiSchema}
                updateValue={(val, hasError) =>
                  handleBannerTextChange(
                    lang.code,
                    'description',
                    val,
                    hasError
                  )
                }
                trim={false}
              />
            </>
          );
        }}
      />

      <PageHeader title='Rack Display' fontSize={FONT_SIZE.LG} />
      {rackDisplays.map((rack, index) => (
        <RackDisplayCard
          key={rack.id}
          index={index}
          rack={rack}
          languages={languages}
          onChange={handleRackChange}
          onRemove={handleRackRemove}
          showRemove={index > 0}
        />
      ))}
      <Button
        handleButton={handleAddRackDisplay}
        uiSchema={{
          title: 'Add Image Card',
          variant: 'tertiary',
          size: 'medium',
          iconLeading: 'add_box',
          className: 'mt-2',
          disabled: rackDisplays.length >= MAX_RACK_DISPLAYS
        }}
      />
    </PageContainer>
  );
};

PerfectStoreDisplay.propTypes = {
  formData: PropTypes.object.isRequired,
  setFormData: PropTypes.func.isRequired,
  languages: PropTypes.array.isRequired
};

export default PerfectStoreDisplay;
